import React, { useMemo } from 'react';
import DateTimePickerInput from './DateTimePickerInput';
import { addDaysToDateString, isWeekendDateString, resolveMultiDayDates } from '../utils/dateHelpers';
import { useDateFormatter } from '../hooks/useDateFormatter';

export const MAX_MULTI_DAY_RANGE = 90;

interface MultiDayDateRangeFieldsProps {
  rangeStartDate: string;
  rangeEndDate: string;
  includeWeekends: boolean;
  onRangeStartChange: (value: string) => void;
  onRangeEndChange: (value: string) => void;
  onIncludeWeekendsChange: (checked: boolean) => void;
  maxDate?: string;
  excludeDate?: string;
  error?: string;
  layout?: 'default' | 'wide';
}

const MultiDayDateRangeFields: React.FC<MultiDayDateRangeFieldsProps> = ({
  rangeStartDate,
  rangeEndDate,
  includeWeekends,
  onRangeStartChange,
  onRangeEndChange,
  onIncludeWeekendsChange,
  maxDate,
  excludeDate,
  error,
  layout = 'default',
}) => {
  const { formatDateOnly, getToday } = useDateFormatter();
  const effectiveMaxDate = maxDate || getToday();

  const resolvedDates = useMemo(
    () =>
      resolveMultiDayDates({
        rangeStartDate,
        rangeEndDate,
        maxDate: effectiveMaxDate,
        includeWeekends,
        excludeDate,
      }),
    [rangeStartDate, rangeEndDate, effectiveMaxDate, includeWeekends, excludeDate]
  );

  const isWide = layout === 'wide';

  return (
    <div className={`space-y-5 ${isWide ? 'md:space-y-6' : ''}`}>
      <div
        className={
          isWide
            ? 'grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2 lg:gap-8'
            : 'grid grid-cols-1 gap-4 sm:grid-cols-2'
        }
      >
        <div className="min-w-0">
          <label className="mb-1.5 block text-sm font-medium text-gray-700">Start Date</label>
          <DateTimePickerInput
            value={rangeStartDate}
            onChange={(value) => {
              onRangeStartChange(value);
              if (rangeEndDate && value > rangeEndDate) {
                onRangeEndChange(value);
              }
            }}
            max={effectiveMaxDate}
            showTimeSelect={false}
          />
        </div>
        <div className="min-w-0">
          <label className="mb-1.5 block text-sm font-medium text-gray-700">End Date</label>
          <DateTimePickerInput
            value={rangeEndDate}
            onChange={onRangeEndChange}
            min={rangeStartDate || undefined}
            max={effectiveMaxDate}
            showTimeSelect={false}
          />
        </div>
      </div>

      <div className={`rounded-md border border-gray-200 bg-gray-50/80 px-4 py-3 ${isWide ? 'sm:flex sm:items-start sm:justify-between sm:gap-6' : ''}`}>
        <div>
          <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-800">
            <input
              type="checkbox"
              checked={includeWeekends}
              onChange={(e) => onIncludeWeekendsChange(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Add Entries for Weekend
          </label>
          <p className="mt-1.5 text-xs text-gray-500">
            When unchecked, only weekdays (Monday–Friday) are included. When checked, all dates in the range are included.
          </p>
        </div>
      </div>

      {resolvedDates.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50/50 px-4 py-4">
          <p className="text-sm font-semibold text-gray-900">
            {resolvedDates.length} {resolvedDates.length === 1 ? 'entry' : 'entries'} will be created
            {!includeWeekends ? ' (weekdays only)' : ''}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {resolvedDates.slice(0, isWide ? 14 : 7).map((date) => {
              const isWeekend = includeWeekends && isWeekendDateString(date);
              return (
                <span
                  key={date}
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    isWeekend
                      ? 'bg-red-100 text-red-700 ring-1 ring-red-200'
                      : 'bg-indigo-100 text-indigo-800'
                  }`}
                >
                  {formatDateOnly(`${date}T12:00:00.000Z`)}
                </span>
              );
            })}
            {resolvedDates.length > (isWide ? 14 : 7) && (
              <span className="inline-flex items-center rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                +{resolvedDates.length - (isWide ? 14 : 7)} more
              </span>
            )}
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-700">{error}</p>}
    </div>
  );
};

export function getDefaultMultiDayRange(maxDate: string): { start: string; end: string } {
  const weekBefore = addDaysToDateString(maxDate, -6);
  return {
    start: weekBefore || maxDate,
    end: maxDate,
  };
}

export default MultiDayDateRangeFields;
